import { useCallback, useEffect, useRef, useState } from "react";

type AdSenseLoadStatus = "loading" | "loaded" | "failed";

const AD_LOAD_TIMEOUT = 5000;

export function useAdSenseAd(enabled: boolean) {
  const [status, setStatus] = useState<AdSenseLoadStatus>("loading");
  const [node, setNode] = useState<HTMLElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((el: HTMLElement | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    const insElement = node;

    if (!enabled || !insElement) {
      setStatus("loading");
      return undefined;
    }

    setStatus("loading");

    const cleanup = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      observerRef.current?.disconnect();
      observerRef.current = null;

      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };

    const decide = (): AdSenseLoadStatus | null => {
      if (!insElement) {
        return null;
      }

      const adStatus = insElement.getAttribute("data-ad-status");
      if (adStatus === "filled") {
        return "loaded";
      }
      if (adStatus === "unfilled") {
        return "failed";
      }

      const iframe = insElement.querySelector("iframe");
      if (iframe) {
        const rect = insElement.getBoundingClientRect();
        if (rect.height > 0 && rect.width > 0) {
          return "loaded";
        }
      }

      return null;
    };

    const applyDecision = () => {
      const decision = decide();
      if (!decision) {
        return;
      }
      setStatus(decision);
      cleanup();
    };

    observerRef.current = new MutationObserver(() => {
      applyDecision();
    });
    observerRef.current.observe(insElement, {
      attributes: true,
      attributeFilter: ["data-ad-status", "style", "class"],
      childList: true,
      subtree: true,
    });

    resizeObserverRef.current = new ResizeObserver(() => {
      applyDecision();
    });
    resizeObserverRef.current.observe(insElement);

    applyDecision();

    timeoutRef.current = window.setTimeout(() => {
      setStatus((s) => (s === "loading" ? "failed" : s));
      cleanup();
    }, AD_LOAD_TIMEOUT);

    return cleanup;
  }, [enabled, node]);

  return { status, ref };
}

